import { CreateRoleRequest } from "@api/apiClient";
import { useCreateRole, useDeleteRole, useRoles } from "@api/roles";
import DataGrid from "@components/DataGrid/DataGrid";
import i18n from "@i18n/i18n";
import DeleteIcon from "@mui/icons-material/Delete";
import { Alert, Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, Snackbar, TextField, Tooltip } from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid-premium";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import useResizeObserver from "use-resize-observer";

interface RoleRowModel {
  id: string;
  name?: string;
  description?: string;
}

const RolesGrid = observer(() => {
  const containerRef = useRef(null);
  const { height } = useResizeObserver({ ref: containerRef });
  const [rows, setRows] = useState<RoleRowModel[]>([]);
  const [newRoleName, setNewRoleName] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error" | "warning">("success");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<string | null>(null);
  const getRoles = useRoles();

  const createRole = useCreateRole({
    onSuccess: () => {
      setSnackbarMessage(i18n.t("roles-tab.create.success"));
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      setNewRoleName("");
      getRoles.refetch();
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || i18n.t("roles-tab.create.error");
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    },
  });

  const deleteRole = useDeleteRole({
    onSuccess: () => {
      setSnackbarMessage(i18n.t("roles-tab.delete.success"));
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      getRoles.refetch();
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || i18n.t("roles-tab.delete.error");
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    },
  });

  useEffect(() => {
    if (getRoles.data?.roles) {
      const roleRows: RoleRowModel[] = getRoles.data.roles.map((role, index) => ({
        id: role.name || `role-${index}`,
        name: role.name,
        description: role.description,
      }));
      setRows(roleRows);
    }
  }, [getRoles.data]);

  const handleCreateRole = () => {
    if (!newRoleName.trim()) {
      setSnackbarMessage(i18n.t("roles-tab.create.validation.empty"));
      setSnackbarSeverity("warning");
      setOpenSnackbar(true);
      return;
    }

    const request = new CreateRoleRequest({
      name: newRoleName.trim(),
      description: newRoleName.trim(), // Description will be set with "Custom -" prefix in backend
    });

    createRole.mutate(request);
  };

  const handleDeleteClick = (roleName: string) => {
    setRoleToDelete(roleName);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (roleToDelete) {
      deleteRole.mutate(roleToDelete);
    }
    setDeleteDialogOpen(false);
    setRoleToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setRoleToDelete(null);
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: i18n.t("roles-tab.grid.column.name"),
      flex: 2,
      minWidth: 200,
    },
    {
      field: "description",
      headerName: i18n.t("roles-tab.grid.column.description"),
      flex: 3,
      minWidth: 300,
    },
    {
      field: "actions",
      type: "actions",
      headerName: i18n.t("roles-tab.grid.column.actions"),
      resizable: false,
      width: 100,
      getActions: (params) => {
        const isCustomRole = params.row.description?.startsWith("Custom -");

        if (isCustomRole) {
          return [
            <GridActionsCellItem
              key={`delete-${params.row.id}`}
              icon={
                <Tooltip title={i18n.t("roles-tab.grid.delete-button.tooltip")}>
                  <DeleteIcon />
                </Tooltip>
              }
              label={i18n.t("roles-tab.grid.delete-button.label")}
              onClick={() => handleDeleteClick(params.row.name)}
            />,
          ];
        }
        return [];
      },
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        gap: 2,
      }}
    >
      {/* Add New Role Section */}
      <Card elevation={3} sx={{ width: "100%", "&.MuiPaper-root": { transition: "none important!" } }}>
        <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "1", width: "100%" }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={6}>
                <TextField
                  fullWidth
                  label={i18n.t("roles-tab.create.field.name")}
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleCreateRole();
                    }
                  }}
                  disabled={createRole.isPending}
                />
              </Grid>
              <Grid size={2}>
                <Tooltip title={i18n.t("roles-tab.create.button.add")}>
                  <Button variant="outlined" sx={{ whiteSpace: "nowrap" }} onClick={handleCreateRole} disabled={createRole.isPending || !newRoleName.trim()}>
                    {i18n.t("roles-tab.create.button.add")}
                  </Button>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Roles Grid */}
      <Box
        ref={containerRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          width: "100%",
          position: "relative",
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          loading={getRoles.isFetching}
          getRowId={(row) => row.id}
          sx={{
            height: height ? `${height}px` : "100%",
            width: "100%",
          }}
        />
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar open={openSnackbar} autoHideDuration={3000} onClose={() => setOpenSnackbar(false)} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>{i18n.t("roles-tab.delete.dialog.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{i18n.t("roles-tab.delete.dialog.message", { roleName: roleToDelete })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            {i18n.t("roles-tab.delete.dialog.cancel")}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {i18n.t("roles-tab.delete.dialog.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default RolesGrid;
